<template>
    <div ref="refRoot" class="swipeTutorial">
        <p ref="refText" class="swipeTutorial__text">{{ text }}</p>

        <RiveStateMachine
            ref="refRive"
            class="swipeTutorial__icon"
            src="/rive/swipe-finger.riv"
            :autoBind="true"
            :autoPlay="false"
            stateMachines="State Machine 1"
            artboard="Swipe"
            :fit="Fit.Contain"
            @load="onRiveLoaded"
        />
    </div>
</template>

<script setup>
    import { Fit } from '@rive-app/canvas';
    import gsap from 'gsap';
    import { computed, ref, watch } from 'vue';

    import { useClassToggle } from '@resn/gozer-vue';

    import RiveStateMachine from '~/components/common/RiveStateMachine.vue';

    const props = defineProps({
        active: {
            type: Boolean,
            default: false,
        },
        dir: {
            type: String,
            default: 'x',
        },
        interactionWaitDelay: { type: Number, default: 2 },
    });

    const refRoot = ref(null);
    const refRive = ref(null);
    const refText = ref(null);

    const elements = computed(() => [refText.value, refRive.value.$el]);

    const text = computed(() => {
        return props.dir === 'x' ? 'Swipe to rip the pack' : 'Swipe down to rip the pack';
    });

    const isVertical = computed(() => {
        return props.dir === 'y';
    });

    const onRiveLoaded = (val) => {
        const input = refRive.value.inputs.find((input) => input.name === 'Vertical');

        if (isVertical.value) {
            input.value = true;
        } else {
            input.value = false;
        }
    };

    let tl;
    const setActive = (val) => {
        tl?.kill();
        tl = gsap.timeline({});

        if (val) {
            refRive.value?.play();
            tl.fromTo(
                elements.value,
                {
                    y: 15,
                    alpha: 0,
                },
                {
                    y: 0,
                    alpha: 1,
                    duration: 0.8,
                    ease: 'power2.out',
                    delay: 0.5,
                    stagger: 0.025,
                }
            );
        } else {
            refRive.value?.pause();
            tl.to(elements.value, {
                alpha: 0,
                duration: 0.2,
            });
        }
    };

    const interacting = ref(false);
    const active = computed(() => props.active && !interacting.value);

    useClassToggle(refRoot, { active });

    let interactionDelay;
    const restartInteraction = () => {
        interacting.value = true;
        interactionDelay?.kill();
        interactionDelay = gsap.delayedCall(props.interactionWaitDelay, () => {
            interacting.value = false;
        });
    };

    const startInteraction = () => {
        interacting.value = true;
        interactionDelay?.kill();
    };

    const stopInteraction = () => {
        interacting.value = false;
        interactionDelay?.kill();
    };

    watch(active, setActive);

    defineExpose({
        startInteraction,
        stopInteraction,
        restartInteraction,
        active,
    });
</script>

<style lang="scss" scoped>
    @use '@/styles/shared.scss' as *;

    .swipeTutorial {
        top: 20%;
        left: 50%;
        position: absolute;
        transform: translate(-50%, -50%);

        @include flexCenter;

        pointer-events: none;

        &__text {
            @include textLabel;
            white-space: nowrap;
            opacity: 0;
        }

        &__icon {
            @include square(14rem);

            margin-top: -2.5rem;
            position: relative;
            opacity: 0;
        }
    }
</style>
